/**
 * useCrmSlideout — returns a click handler that opens the CRM slideout
 * for a given entity ID. Returns undefined when no entity ID is provided
 * or no slideout context is available.
 *
 * Origin-aware (see `useOpenSlideout`): layers only when the click comes
 * from within a rendered entity/thread slideout; replaces otherwise.
 *
 * Usage:
 *   const handleClick = useCrmSlideout(entityId);
 *   <ContactCardContent onClick={handleClick} />
 */

import { useCallback } from 'react';

import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';

export function useCrmSlideout(
  entityId: string | undefined
): (() => void) | undefined {
  const openSlideout = useOpenSlideoutOptional();

  const handler = useCallback(() => {
    if (entityId && openSlideout) {
      openSlideout(entityId);
    }
  }, [entityId, openSlideout]);

  if (!entityId || !openSlideout) {
    return undefined;
  }

  return handler;
}
